import { AtsCandidateFormVisibilityTypeEnum } from '@crehana/compass.knowledgehub/types';

import { Button } from '@crehana/compass.ui/button';
import { toast } from '@crehana/compass.ui/toaster';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@crehana/forms/components';
import { FormProvider, useForm, zodResolver } from '@crehana/forms/rhf';
import { useEffect, useState } from 'react';

import type { TPageLoaderVacancyData } from '@/p/views/candidate-form/types';
import { clsx } from 'clsx';
import { useAddResourceBundle } from '../i18n/use-add-resource-bundle';
import { useTranslationSharedCandidateForm } from '../i18n/use-translations';
import type { TCandidateForm } from '../types';
import { EmailValidationPopUp } from './email-validation-pop-up';
import { GeneralInformation } from './general-information';
import { LinkedInValidationPopUp } from './linkedin-validation-pop-up';
import { Questions } from './questions';
import { candidateFormSchema } from './schemas';

export type TFormSubmitResult = {
  success?: boolean;
  emailExists?: boolean;
  invalidLinkedin?: boolean;
  validationError?: boolean;
  error?: boolean;
};

type TFormProps = {
  type: 'internal' | 'external';
  jobVacancyPublicInfo: TPageLoaderVacancyData;
  organizationId: number;
  preview: boolean;
  onSubmitForm?: (data: TCandidateForm) => Promise<TFormSubmitResult>;
  loading?: boolean;
  onValidityChange?: (isValid: boolean) => void;
};

export const Form = ({
  type,
  jobVacancyPublicInfo,
  organizationId,
  preview = false,
  onSubmitForm,
  loading = false,
  onValidityChange,
}: TFormProps) => {
  useAddResourceBundle();
  const { t } = useTranslationSharedCandidateForm();
  //Pop up to validate the email
  const [openEmailValidation, setOpenEmailValidation] = useState(false);
  //Pop up to validate the linkedin url
  const [openLinkedInValidation, setOpenLinkedInValidation] = useState(false);
  //Get the personal info map
  const personalInfoMap = Object.fromEntries(
    jobVacancyPublicInfo?.personal_info?.map(item => [
      item.label,
      item.visibility,
    ]) || [],
  );

  //Create the form
  const form = useForm<TCandidateForm>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      last_name: '',
      email: '',
      number_phone: '+',
      country: '',
      linkedin_url: null,
      resume: '',
      candidate_source: undefined,
      disability: undefined,
      gender: undefined,
      answers:
        jobVacancyPublicInfo?.questions?.map(q => ({
          question_id: Number(q.id),
          question_type: q.question_type,
          question_name: q.title,
          allow_multiple_responses: q.allow_multiple_responses,
          question_options: q.question_options || undefined,
          answer_value: {
            value: '',
            is_required: q.is_required,
            response_size: q.response_size || undefined,
          },
        })) || [],
    },
    resolver: zodResolver(
      candidateFormSchema({
        number_phone:
          personalInfoMap['PERSONAL_INFO_PHONE'] ===
          AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility,
        country:
          personalInfoMap['PERSONAL_INFO_COUNTRY'] ===
          AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility,
        resume:
          personalInfoMap['PERSONAL_INFO_RESUME'] ===
            AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility ||
          personalInfoMap['PERSONAL_INFO_RESUME'] ===
            AtsCandidateFormVisibilityTypeEnum.AlwaysRequiredCandidateFormVisibility,
        linkedin_url:
          personalInfoMap['PERSONAL_INFO_LINKEDIN'] ===
          AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility,
        candidate_source:
          personalInfoMap['PERSONAL_INFO_CANDIDATE_SOURCE'] ===
          AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility,
        disability:
          personalInfoMap['PERSONAL_INFO_DISABILITY'] ===
          AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility,
        gender:
          personalInfoMap['PERSONAL_INFO_DIVERSITY_GENDER'] ===
          AtsCandidateFormVisibilityTypeEnum.RequiredCandidateFormVisibility,
      }),
    ),
  });

  const { control, formState } = form;

  // Notify parent component when form validity changes
  useEffect(() => {
    onValidityChange?.(formState.isValid);
  }, [formState.isValid, onValidityChange]);

  const onSubmit = async (data: TCandidateForm) => {
    // Validate phone number length (min 5, max 15) before submitting
    const phoneNumber =
      data.number_phone?.replace(/^\+/, '').replace(/\D/g, '') || '';
    if (phoneNumber && (phoneNumber.length < 5 || phoneNumber.length > 15)) {
      toast.error(t('INVALID_PHONE_LENGTH'));
      return;
    }

    if (!onSubmitForm) {
      return;
    }

    try {
      const result = await onSubmitForm(data);

      if (result.success) {
        return;
      }
      if (result.emailExists) {
        setOpenEmailValidation(true);
        return;
      }
      if (result.invalidLinkedin) {
        setOpenLinkedInValidation(true);
        return;
      }
      if (result.validationError) {
        toast.error(t('VALIDATION_ERROR'));
        return;
      }

      toast.error(t('ERROR_TO_CREATE_JOB_APPLICANT_PROFILE'));
    } catch (error) {
      console.error('Error to create a job applicant profile', error);
      toast.error(t('ERROR_TO_CREATE_JOB_APPLICANT_PROFILE'));
    }
  };

  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-text-center">
      <div className="tw-flex tw-text-start tw-flex-col">
        <p className="tw-font-body4 tw-text-neutral-light-700">
          {t('REQUIRED_FIELDS')}
          <span className="tw-text-alert-500">{t('REQUIRED_ICON')}</span>
        </p>
        <FormProvider {...form}>
          <form
            id={type === 'internal' ? 'internal-candidate-form' : undefined}
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {/* General information */}
            <GeneralInformation
              type={type}
              jobVacancyPublicInfo={jobVacancyPublicInfo}
              organizationId={organizationId || 0}
              preview={preview}
            />
            {/* Questions */}
            {form
              .watch('answers')
              .sort((a, b) => a.question_id - b.question_id)
              .map((question, index) => (
                <FormField
                  key={question.question_id}
                  control={control}
                  name={`answers.${index}.answer_value`}
                  render={({ field, fieldState: { error } }) => (
                    <FormItem className="tw-mt-3.2">
                      <FormLabel>
                        <span
                          className={clsx(
                            type === 'internal'
                              ? 'tw-cds-font-subtitle-01'
                              : 'tw-font-subtitle4',
                          )}
                        >
                          {question.question_name.replace(/\*$/, '')}
                          {question.answer_value.is_required && (
                            <span className="tw-text-alert-500">
                              {t('REQUIRED_ICON')}
                            </span>
                          )}
                        </span>
                      </FormLabel>
                      <FormControl>
                        <Questions
                          question_id={question.question_id.toString()}
                          type={question.question_type}
                          value={field.value.value}
                          onChange={val => {
                            field.onChange({
                              ...field.value,
                              value: val,
                              is_required: question.answer_value.is_required,
                              response_size:
                                question.answer_value.response_size,
                            });
                          }}
                          response_size={question.answer_value.response_size}
                          allow_multiple_responses={
                            question.allow_multiple_responses
                          }
                          question_options={question.question_options}
                          error={!!error}
                          organizationId={organizationId || 0}
                          preview={preview}
                        />
                      </FormControl>
                      {error && <FormMessage>{error.message}</FormMessage>}
                    </FormItem>
                  )}
                />
              ))}
            {type === 'external' && (
              <div className="tw-flex  tw-justify-center tw-mt-5.6 tw-mb-7.2">
                <Button
                  size="lg"
                  type="submit"
                  disabled={!form.formState.isValid || loading || preview}
                  loading={loading}
                  loadingText={t('LOADING_SEND_APPLICATION')}
                >
                  {t('SEND_APPLICATION')}
                </Button>
              </div>
            )}
          </form>
        </FormProvider>
      </div>
      <EmailValidationPopUp
        email={form.getValues('email')}
        open={openEmailValidation}
        onOpenChange={setOpenEmailValidation}
      />
      <LinkedInValidationPopUp
        linkedinUrl={form.getValues('linkedin_url') || ''}
        open={openLinkedInValidation}
        onOpenChange={setOpenLinkedInValidation}
      />
    </div>
  );
};
